import { CommonModule } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { IonicModule } from '@ionic/angular';

import { languageService, languageSupport, viewService } from '@unpispas/upp-base';
import {
    paytefService,
    PinpadCharge,
    PinpadCause,
    PinpadOutcome,
    PinpadProgress,
    PinpadRefundRequest,
    PinpadResult,
    PinpadStage,
} from '@unpispas/feature/drivers';
import type { PinpadPay } from '@unpispas/upp-data';
import { Pinpad } from '@unpispas/upp-data';
import { TicketView } from '@unpispas/upp-data';
import { UppWdgtModule } from '@unpispas/upp-wdgt';

/** Which operation the dialog runs: a sale charge or a refund (F-PTF09-04). */
type PinpadOperation = 'SALE' | 'REFUND';

/** Action the dialog starts with; `MANUAL` checks a pending attempt before declaring it. */
export type PinpadAction = 'RETRY' | 'MANUAL';

type PinpadScreen = 'WAITING' | 'WAITING_IDLE' | 'CONFIRM' | 'RUNNING' | 'APPROVED' | 'FAILED';

/**
 * Output of the panel. On sale `APPROVED`/`MANUAL` the panel already ran
 * {@link PinpadCharge.Settle} (or {@link paytefService.SettleSales} when there was no live session);
 * `applied === false` means settle failed. Refunds leave `CloseRefund` to the host.
 */
export type PinpadDone = {
    outcome: 'APPROVED' | 'MANUAL' | 'CLOSED';
    result: PinpadResult | null;
    /** Catalog key the host shows as a brief warning once the panel is gone. */
    notice?: string;
    /** Sale only: false when Apply/Settle did not close the ticket. */
    applied?: boolean;
};

/**
 * Embedded Paytef charge/refund panel (hosted in `upp-dialog` by ticket edit/thumb).
 * Status-first: unresolved attempts are polled before any new charge.
 */
@Component({
    selector: 'upp-pinpad-payment',
    standalone: true,
    imports: [CommonModule, IonicModule, UppWdgtModule],
    templateUrl: './upp-payment.html',
    styleUrls: ['./upp-payment.scss'],
})
export class UppPinpadPaymentComponent extends languageSupport implements OnInit, OnDestroy {
    /** Poll interval while waiting for a terminal answer on a pending attempt. */
    private static readonly _POLL_MS = 5000;
    /** Max consecutive UNKNOWN polls before idle (90 s). IN_PROGRESS does not count. */
    private static readonly _POLL_MAX_UNKNOWN = 18;

    /********************************/
    /* INPUTS / OUTPUTS            */
    /********************************/

    @Input() tickets: TicketView[] = [];
    /** Destination Paytef terminal. */
    @Input() terminal: Pinpad | null = null;
    /** Sale or refund panel (DD-09-11); drives texts, amount and the service call it makes. */
    @Input() operation: PinpadOperation = 'SALE';
    /** Refund payload required when `operation === 'REFUND'`; ignored for a sale. */
    @Input() refund: PinpadRefundRequest | null = null;
    /** Action `ngOnInit` starts with; `MANUAL` checks a pending attempt before declaring it. */
    @Input() action: PinpadAction = 'RETRY';
    @Output() OnDone = new EventEmitter<PinpadDone>();

    /********************************/
    /* STATE                       */
    /********************************/

    screen: PinpadScreen = 'RUNNING';
    statusKey = '@ticket_cardterm_status_connecting';
    failKey: string | null = null;
    pendingHint = false;
    pendingAction: PinpadAction = 'RETRY';
    alreadyApproved = false;
    private _result: PinpadResult | null = null;
    private _charge: PinpadCharge | null = null;
    private _progressSub: { unsubscribe: () => void } | null = null;
    private _closeTimer: ReturnType<typeof setTimeout> | null = null;
    private _pollTimer: ReturnType<typeof setTimeout> | null = null;
    private _pollUnknown = 0;
    private _polling = false;
    private _destroyed = false;
    private _cancelBusy = false;
    private _starting = false;
    private _busy = false;
    private _manualRunning = false;

    constructor(
        private readonly lang: languageService,
        public readonly view: viewService,
        private readonly paytef: paytefService,
        private readonly cdr: ChangeDetectorRef,
    ) {
        super(lang, null);
    }

    private static _BuildResult(
        outcome: PinpadOutcome,
        cause: PinpadCause | null,
        row: PinpadPay | null = null,
    ): PinpadResult {
        return {
            outcome,
            cause,
            canRetry: outcome !== 'APPROVED' && cause !== 'AMOUNT_MISMATCH',
            row,
        };
    }

    /********************************/
    /* UI GETTERS                  */
    /********************************/

    /** True while the panel cannot be dismissed (waiting, confirming or charging). */
    get IsRunning(): boolean {
        return this.screen === 'RUNNING' || this.screen === 'WAITING' || this.screen === 'CONFIRM';
    }

    /** Retry only on a clean failure (no unresolved PE). */
    get CanRetry(): boolean {
        return this._result?.canRetry === true && this._result?.outcome !== 'PENDING';
    }

    /** Header close (X) on FAILED / CONFIRM / WAITING_IDLE. */
    get CanClose(): boolean {
        return this.screen === 'FAILED' || this.screen === 'CONFIRM' || this.screen === 'WAITING_IDLE';
    }

    get CanManual(): boolean {
        return !!this.terminal && !!this.tickets?.length;
    }

    get Busy(): boolean {
        return this._busy || this._polling;
    }

    get CancelDisabled(): boolean {
        return this._busy || this._cancelBusy
            || this.statusKey === '@ticket_cardterm_status_cancelling'
            || this.statusKey === '@ticket_cardterm_refund_status_cancelling';
    }

    get ManualDisabled(): boolean {
        return this._busy || this._polling || (this.screen === 'RUNNING' && this.CancelDisabled);
    }

    get TerminalLabel(): string {
        return this.terminal?.PinpadName || '';
    }

    /**
     * Terminal name is useful while charging or after a clean failure (multi-terminal POS).
     * Hidden while waiting for status: the waiter already knows which device is in play.
     */
    get ShowTerminalLabel(): boolean {
        if (!this.TerminalLabel) {
            return false;
        }
        return this.screen === 'RUNNING' || this.screen === 'FAILED' || this.screen === 'APPROVED';
    }

    get AmountToPay(): number {
        if (this.operation === 'REFUND') {
            return (this.refund?.amountCents ?? 0) / 100;
        }

        let _total = 0;
        for (const _ticket of this.tickets) {
            _total += Number(_ticket.due ?? _ticket.price ?? 0);
        }
        return _total;
    }

    price(value: number): string {
        const _price = Math.round(value * 100) / 100;
        const _parts = Math.abs(_price).toString().split('.');
        const _int = _parts[0];
        const _dec = _parts.length > 1 ? (_parts[1] + '0').slice(0, 2) : '00';
        return _int + ',' + _dec;
    }

    /********************************/
    /* LIFECYCLE                   */
    /********************************/

    ngOnInit(): void {
        void this._Start(this.action);
    }

    ngOnDestroy(): void {
        this._destroyed = true;
        this._StopPoll();
        this._ClearCloseTimer();
        this._progressSub?.unsubscribe();
        this._progressSub = null;
    }

    /********************************/
    /* PUBLIC HANDLERS             */
    /********************************/

    OnCancelClick(): void {
        if (this.CancelDisabled || !this._charge) {
            return;
        }
        (document.activeElement as HTMLElement)?.blur();
        this._cancelBusy = true;
        this.statusKey = this.operation === 'REFUND'
            ? '@ticket_cardterm_refund_status_cancelling'
            : '@ticket_cardterm_status_cancelling';
        this._Mark();
        void this._charge.Cancel();
    }

    async OnRetryClick(): Promise<void> {
        if (this._busy || this.screen !== 'FAILED' || !this.CanRetry) {
            return;
        }
        (document.activeElement as HTMLElement)?.blur();
        this._result = null;
        await this._Start('RETRY');
    }

    async OnManualClick(): Promise<void> {
        if (this.ManualDisabled || !this.CanManual) {
            return;
        }
        if (this.screen === 'RUNNING') {
            await this._ManualDuringCharge();
            return;
        }
        if (this.screen === 'FAILED') {
            (document.activeElement as HTMLElement)?.blur();
            await this._Start('MANUAL');
            return;
        }
        if (this.screen === 'WAITING' || this.screen === 'WAITING_IDLE') {
            (document.activeElement as HTMLElement)?.blur();
            this._StopPoll();
            this.pendingAction = 'MANUAL';
            this.screen = 'CONFIRM';
            this._Mark();
        }
    }

    OnConsultAgainClick(): void {
        if (this._busy || this.screen !== 'WAITING_IDLE') {
            return;
        }
        (document.activeElement as HTMLElement)?.blur();
        void this._EnterWaiting(this.pendingAction);
    }

    async OnConfirmClick(): Promise<void> {
        const _terminal = this.terminal;
        if (this._busy || this.screen !== 'CONFIRM' || !_terminal) {
            return;
        }
        (document.activeElement as HTMLElement)?.blur();
        this._busy = true;
        this._Mark();
        const _kind = this.operation === 'REFUND' ? 'RE' : 'SA';
        try {
            await this._Declare(_terminal, _kind);
        } finally {
            this._busy = false;
            this._Mark();
        }
    }

    OnCloseClick(): void {
        (document.activeElement as HTMLElement)?.blur();
        this._StopPoll();
        void this._EmitDone({ outcome: 'CLOSED', result: this._result });
    }

    /********************************/
    /* START AND CHARGE FLOW       */
    /********************************/

    /**
     * Single start path of both actions: an unresolved attempt is checked / polled on the terminal
     * before a new charge (`RETRY`) or a manual declaration (`MANUAL`). Without a pending attempt
     * the terminal is not queried.
     */
    private async _Start(action: PinpadAction): Promise<void> {
        if (this._starting) {
            return;
        }

        const _terminal = this.terminal;
        const _ticket = this.tickets?.[0];
        if (!_terminal || !_ticket) {
            this._ShowFailed(UppPinpadPaymentComponent._BuildResult('NOSERVICE', 'NO_SERVICE', null));
            return;
        }

        const _kind = this.operation === 'REFUND' ? 'RE' : 'SA';
        this.pendingAction = action;
        this._starting = true;
        this._busy = true;
        try {
            const _pendingRow = _kind === 'RE' ? _ticket.RefundPending : _ticket.PinpadPending;
            const _unresolved = _kind === 'RE' ? !!_pendingRow : !!(_pendingRow || _ticket.PinpadToApply);
            if (_unresolved) {
                this.screen = 'WAITING';
                this._Mark();

                const _check = await this.paytef.CheckPrevious(this.tickets, _terminal, _kind);
                if (this._destroyed) {
                    return;
                }

                switch (_check) {
                    case 'APPROVED': {
                        const _approvedRow = _kind === 'RE' ? _ticket.RefundToApply : _ticket.PinpadToApply;
                        this._ShowApproved(
                            UppPinpadPaymentComponent._BuildResult('APPROVED', null, _approvedRow),
                            true,
                        );
                        return;
                    }
                    case 'MISMATCH':
                        if (action === 'RETRY') {
                            this._ShowFailed(
                                UppPinpadPaymentComponent._BuildResult('MISMATCH', 'AMOUNT_MISMATCH', _pendingRow),
                            );
                            return;
                        }
                        break;
                    case 'UNCERTAIN':
                    case 'IN_PROGRESS':
                        this._starting = false;
                        this._busy = false;
                        await this._EnterWaiting(action);
                        return;
                    default:
                        break;
                }
            }

            if (action === 'MANUAL') {
                await this._Declare(_terminal, _kind);
            }
            else {
                this._Charge(_terminal);
            }
        } finally {
            this._starting = false;
            this._busy = false;
            this._Mark();
        }
    }

    /**
     * Polls {@link paytefService.Resolve} until the pending attempt is settled, in progress keeps
     * waiting without burning the idle budget, or UNKNOWN exhausts the window → WAITING_IDLE.
     */
    private async _EnterWaiting(action: PinpadAction): Promise<void> {
        const _terminal = this.terminal;
        if (!_terminal || this._destroyed) {
            return;
        }

        this.pendingAction = action;
        this._StopPoll();
        this._pollUnknown = 0;
        this.screen = 'WAITING';
        this.pendingHint = true;
        this._result = UppPinpadPaymentComponent._BuildResult(
            'PENDING',
            this._result?.cause ?? 'LOST_TRACKING',
            this._result?.row ?? null,
        );
        this._Mark();
        await this._PollOnce();
    }

    private async _PollOnce(): Promise<void> {
        const _terminal = this.terminal;
        if (!_terminal || this._destroyed || this.screen !== 'WAITING') {
            return;
        }

        this._polling = true;
        this._Mark();
        const _kind = this.operation === 'REFUND' ? 'RE' : 'SA';
        try {
            const _outcome = await this.paytef.Resolve(this.tickets, _terminal, _kind);
            if (this._destroyed || this.screen !== 'WAITING') {
                return;
            }

            const _ticket = this.tickets[0];
            const _row = _kind === 'RE' ? _ticket?.RefundPending ?? _ticket?.RefundToApply : _ticket?.PinpadPending ?? _ticket?.PinpadToApply;

            if (_outcome === 'APPROVED') {
                this._StopPoll();
                this._ShowApproved(
                    UppPinpadPaymentComponent._BuildResult('APPROVED', null, _row ?? null),
                    true,
                );
                return;
            }

            if (_outcome === 'DENIED') {
                this._StopPoll();
                if (this.pendingAction === 'MANUAL') {
                    await this._Declare(_terminal, _kind);
                    return;
                }
                this._ShowFailed(
                    UppPinpadPaymentComponent._BuildResult('DENIED', 'DENIED', _row ?? null),
                );
                return;
            }

            if (_outcome === 'IN_PROGRESS') {
                this._SchedulePoll();
                return;
            }

            this._pollUnknown += 1;
            if (this._pollUnknown >= UppPinpadPaymentComponent._POLL_MAX_UNKNOWN) {
                this._StopPoll();
                this.screen = 'WAITING_IDLE';
                this.pendingHint = true;
                this._Mark();
                return;
            }

            this._SchedulePoll();
        } finally {
            this._polling = false;
            this._Mark();
        }
    }

    private _SchedulePoll(): void {
        this._ClearPollTimer();
        this._pollTimer = setTimeout(() => {
            this._pollTimer = null;
            void this._PollOnce();
        }, UppPinpadPaymentComponent._POLL_MS);
    }

    private _StopPoll(): void {
        this._ClearPollTimer();
        this._polling = false;
    }

    private _ClearPollTimer(): void {
        if (this._pollTimer) {
            clearTimeout(this._pollTimer);
            this._pollTimer = null;
        }
    }

    /**
     * Gives up waiting for the terminal: the charge cancels the sale, reads its final result and
     * declares the payment manual unless it was already approved (shown as approved by
     * `_OnResult`). The panel is never left stuck, even if the terminal does not answer.
     */
    private async _ManualDuringCharge(): Promise<void> {
        const _charge = this._charge;
        if (!_charge) {
            return;
        }

        (document.activeElement as HTMLElement)?.blur();
        this._busy = true;
        this._manualRunning = true;
        this.statusKey = this.operation === 'REFUND'
            ? '@ticket_cardterm_refund_status_cancelling'
            : '@ticket_cardterm_status_cancelling';
        this._Mark();
        try {
            const _manual = await _charge.Manual();
            if (!this._destroyed && _manual.outcome === 'MANUAL') {
                await this._EmitDone({
                    outcome: 'MANUAL',
                    result: this._result,
                    notice: _manual.cancelConfirmed
                        ? undefined
                        : (this.operation === 'REFUND'
                            ? '@ticket_cardterm_refund_manual_uncancelled'
                            : '@ticket_cardterm_manual_uncancelled'),
                });
            }
        } finally {
            this._manualRunning = false;
            this._busy = false;
            this._Mark();
        }
    }

    /** Records the waiter's manual declaration; sale settle runs inside {@link _EmitDone}. */
    private async _Declare(terminal: Pinpad, kind: 'SA' | 'RE'): Promise<void> {
        await this.paytef.DeclareManual(this.tickets, terminal, kind);
        if (!this._destroyed) {
            await this._EmitDone({ outcome: 'MANUAL', result: this._result });
        }
    }

    /**
     * Starts the terminal session: a sale charge, or a refund when {@link refund} carries the
     * request. A refund panel opened without that payload never reaches the terminal (DD-09-11).
     */
    private _Charge(terminal: Pinpad): void {
        if (this.operation === 'REFUND' && !this.refund) {
            console.error('[PAYTEF] refund panel opened without a refund request');
            this._ShowFailed(UppPinpadPaymentComponent._BuildResult('FAILED', 'UNKNOWN', null));
            return;
        }

        this._progressSub?.unsubscribe();
        this.screen = 'RUNNING';
        this.statusKey = '@ticket_cardterm_status_connecting';
        this.failKey = null;
        this.pendingHint = false;
        this.alreadyApproved = false;
        this._cancelBusy = false;
        this._result = null;
        this._Mark();

        this._charge = this.operation === 'REFUND' && this.refund
            ? this.paytef.Refund(this.tickets[0], terminal, this.refund)
            : this.paytef.Charge(this.tickets, terminal);
        this._progressSub = this._charge.Progress$.subscribe((progress) => {
            this._OnProgress(progress);
        });

        void this._charge.Result.then((result) => {
            this._OnResult(result);
        });
    }

    private _OnProgress(progress: PinpadProgress): void {
        if (this._destroyed || this.screen !== 'RUNNING') {
            return;
        }
        this.statusKey = this._StatusKey(progress.stage);
        this._Mark();
    }

    private _OnResult(result: PinpadResult): void {
        if (this._destroyed) {
            return;
        }

        const _cancelRequested = this._cancelBusy;
        this._result = result;
        this._cancelBusy = false;

        if (result.outcome === 'APPROVED') {
            this._ShowApproved(result, !!result.approvedAfterCancel);
            return;
        }

        if (this._manualRunning) {
            return;
        }

        if (result.outcome === 'CANCELLED') {
            void this._EmitDone({ outcome: 'CLOSED', result });
            return;
        }

        if (_cancelRequested && result.outcome === 'PENDING') {
            void this._EmitDone({
                outcome: 'CLOSED',
                result,
                notice: this.operation === 'REFUND'
                    ? '@ticket_cardterm_refund_cancel_unconfirmed'
                    : '@ticket_cardterm_cancel_unconfirmed',
            });
            return;
        }

        if (result.outcome === 'PENDING') {
            void this._EnterWaiting(this.pendingAction);
            return;
        }

        this._ShowFailed(result);
    }

    private _ShowApproved(result: PinpadResult, alreadyApproved: boolean): void {
        this._StopPoll();
        this._result = result;
        this.screen = 'APPROVED';
        this.statusKey = this.operation === 'REFUND'
            ? '@ticket_cardterm_refund_status_approved'
            : '@ticket_cardterm_status_approved';
        this.alreadyApproved = alreadyApproved;
        this.pendingHint = false;
        this._Mark();
        this._closeTimer = setTimeout(() => {
            this._closeTimer = null;
            if (!this._destroyed) {
                void this._EmitDone({ outcome: 'APPROVED', result });
            }
        }, 1500);
    }

    /**
     * Emits {@link OnDone}. For sale APPROVED/MANUAL, settles the ticket(s) first
     * (`PinpadCharge.Settle` or `paytef.SettleSales` when there was no live session).
     */
    private async _EmitDone(done: PinpadDone): Promise<void> {
        if (
            this.operation === 'SALE'
            && (done.outcome === 'APPROVED' || done.outcome === 'MANUAL')
        ) {
            let _applied = false;
            if (this._charge) {
                _applied = await this._charge.Settle();
            }
            else if (this.tickets.length) {
                _applied = await this.paytef.SettleSales(this.tickets);
            }
            done = {
                ...done,
                applied: _applied,
                notice: _applied
                    ? done.notice
                    : (done.notice ?? '@ticket_cardterm_pending_ticket'),
            };
        }

        this.OnDone.emit(done);
    }

    private _ShowFailed(result: PinpadResult): void {
        this._StopPoll();
        this.screen = 'FAILED';
        this.failKey = this._FailKey(result.cause);
        this.pendingHint = false;
        this._result = result;
        this._Mark();
    }

    /********************************/
    /* STATUS AND HELPERS          */
    /********************************/

    /** Catalog key for a terminal progress stage (sale vs refund where the copy differs). */
    private _StatusKey(stage: PinpadStage): string {
        const _refund = this.operation === 'REFUND';
        switch (stage) {
            case 'CONNECTING':
                return '@ticket_cardterm_status_connecting';
            case 'WAITING_CARD':
                return '@ticket_cardterm_status_waiting_card';
            case 'PIN':
                return '@ticket_cardterm_status_pin';
            case 'PIN_RETRY':
                return '@ticket_cardterm_status_pin_retry';
            case 'CURRENCY':
                return '@ticket_cardterm_status_currency';
            case 'SIGNATURE':
                return '@ticket_cardterm_status_signature';
            case 'CUSTOMER':
                return '@ticket_cardterm_status_customer';
            case 'CANCELLING':
                return _refund
                    ? '@ticket_cardterm_refund_status_cancelling'
                    : '@ticket_cardterm_status_cancelling';
            case 'APPROVED':
                return _refund
                    ? '@ticket_cardterm_refund_status_approved'
                    : '@ticket_cardterm_status_approved';
            case 'PROCESSING':
            default:
                return _refund
                    ? '@ticket_cardterm_refund_status_processing'
                    : '@ticket_cardterm_status_processing';
        }
    }

    /**
     * Catalog key for a failure cause. Neutral causes keep the sale key in refund mode;
     * refund-specific causes use `@ticket_cardterm_refund_*` (RN-P09-07).
     */
    private _FailKey(cause: PinpadCause | null): string {
        const _refund = this.operation === 'REFUND';
        switch (cause ?? 'UNKNOWN') {
            case 'NO_SERVICE':
                return '@ticket_cardterm_err_no_service';
            case 'UNREACHABLE':
                return '@ticket_cardterm_err_unreachable';
            case 'TERMINAL_OFFLINE':
                return '@ticket_cardterm_err_terminal_offline';
            case 'PINPAD_DOWN':
                return '@ticket_cardterm_err_paytef_down';
            case 'UNAUTHORIZED':
                return '@ticket_cardterm_err_unauthorized';
            case 'BUSY':
                return '@ticket_cardterm_err_busy';
            case 'TIMEOUT':
                return '@ticket_cardterm_err_timeout';
            case 'START_FAILED':
                return _refund
                    ? '@ticket_cardterm_refund_err_start_failed'
                    : '@ticket_cardterm_err_start_failed';
            case 'LOST_TRACKING':
                return _refund
                    ? '@ticket_cardterm_refund_err_lost_tracking'
                    : '@ticket_cardterm_err_lost_tracking';
            case 'NO_RESULT':
                return _refund
                    ? '@ticket_cardterm_refund_err_no_result'
                    : '@ticket_cardterm_err_no_result';
            case 'TERMINAL_CANCELLED':
                return _refund
                    ? '@ticket_cardterm_refund_err_terminal_cancelled'
                    : '@ticket_cardterm_err_terminal_cancelled';
            case 'DENIED':
                return _refund
                    ? '@ticket_cardterm_refund_err_denied'
                    : '@ticket_cardterm_err_denied';
            case 'AMOUNT_MISMATCH':
                return _refund
                    ? '@ticket_cardterm_refund_err_amount_mismatch'
                    : '@ticket_cardterm_err_amount_mismatch';
            case 'UNKNOWN':
            default:
                return _refund ? '@ticket_cardterm_refund_failed' : '@ticket_cardterm_failed';
        }
    }

    private _ClearCloseTimer(): void {
        if (this._closeTimer) {
            clearTimeout(this._closeTimer);
            this._closeTimer = null;
        }
    }

    private _Mark(): void {
        if (!this._destroyed) {
            this.cdr.markForCheck();
        }
    }
}
